import { type ComponentPropsWithoutRef, useState } from 'react';
import { SUBTENANT_PARENT_NAME_SEPARATOR, TENANT_NAME_PREFIX } from '../../constants/tenant';
import translations from '../../translations';
import TenantNameInput from './TenantNameInput';

type DeveloperNameProps = {
    tenantName: string;
    parentTenantName: string;
    onValidate: (isValid: boolean) => void;
    onChange: (value: string) => void;
} & Pick<ComponentPropsWithoutRef<typeof TenantNameInput>, 'showValidation'>;

// Remove the tenant name prefix and parent name suffix
const isolateTenantName = (name: string, isolatedParentName = '') => {
    return name
        .replace(TENANT_NAME_PREFIX, '')
        .replace(`${SUBTENANT_PARENT_NAME_SEPARATOR}${isolatedParentName}`, '');
};

const DeveloperName = ({
    tenantName,
    parentTenantName,
    showValidation = true,
    onChange,
    onValidate,
}: DeveloperNameProps) => {
    const isolatedParentName = isolateTenantName(parentTenantName);
    const isolatedTenantName = isolateTenantName(tenantName, isolatedParentName);

    const isSubtenant = parentTenantName.length > 0;

    const [nameInput, setNameInput] = useState(isolatedTenantName);

    const handleChange: ComponentPropsWithoutRef<typeof TenantNameInput>['onChange'] = ({
        value,
        isValid,
    }) => {
        setNameInput(value);
        onValidate(isValid);

        const nameToSave = isValid ? value : isolatedTenantName;

        const formattedName = isSubtenant
            ? `${nameToSave}${SUBTENANT_PARENT_NAME_SEPARATOR}${isolatedParentName}`
            : nameToSave;

        onChange(formattedName);
    };

    return (
        <>
            <TenantNameInput
                label={translations.TENANT_name_input_label}
                value={nameInput}
                onChange={handleChange}
                parentTenantName={parentTenantName}
                showValidation={showValidation}
            />
            {isSubtenant ? null : (
                <span className="help-block">{translations.TENANT_subtenant_rename_warning}</span>
            )}
        </>
    );
};

export default DeveloperName;
